import { useEffect, useState } from "react";
import { toast } from "sonner";

import { BottomSheet, ChalkActionButton, SectionTitle } from "./primitives";
import { api } from "../../lib/chalkApi";

const today = () => new Date().toISOString().slice(0, 10);

// Bulk Erase and Redraw (The Chalkboard): cancel, delay or shift every event in a date range.
export default function BulkRedrawSheet({ isOpen, teamId, events, onDismiss, onChanged }) {
  const [action, setAction] = useState("shift");
  const [from, setFrom] = useState(today());
  const [to, setTo] = useState(today());
  const [days, setDays] = useState(0);
  const [minutes, setMinutes] = useState(30);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (isOpen) { setFrom(today()); setTo(today()); setNote(""); } }, [isOpen]);

  const inRange = (events || []).filter((e) => e.status !== "cancelled" && (e.local_time || "").slice(0, 10) >= from && (e.local_time || "").slice(0, 10) <= to);

  async function apply() {
    setBusy(true);
    try {
      const res = await api.bulkEvents(teamId, { action, from_date: from, to_date: to, shift_days: Number(days), shift_minutes: Number(minutes), note });
      toast.success(`${res.count} event${res.count === 1 ? "" : "s"} updated. Families get one combined alert.`);
      if (res.conflicts?.length) toast.warning(`Heads up: now overlaps ${res.conflicts.map((c) => c.title).join(", ")}.`);
      onChanged && onChanged();
      onDismiss && onDismiss();
    } catch (e) {
      toast.error(e.message || "Couldn't update those events.");
    } finally {
      setBusy(false);
    }
  }

  const row = { display: "flex", gap: 10 };
  return (
    <BottomSheet isOpen={isOpen} title="Bulk Erase and Redraw" onDismiss={onDismiss}>
      <div data-testid="bulk-redraw-sheet">
        <SectionTitle label="Date range" />
        <div style={row}>
          <input type="date" className="chalk-input" data-testid="bulk-from" value={from} onChange={(e) => setFrom(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
          <input type="date" className="chalk-input" data-testid="bulk-to" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ flex: 1, minWidth: 0 }} />
        </div>
        <SectionTitle label="What to do" />
        <select className="chalk-input" data-testid="bulk-action" value={action} onChange={(e) => setAction(e.target.value)}>
          <option value="shift">Shift times</option>
          <option value="delay">Mark delayed</option>
          <option value="cancel">Cancel all</option>
        </select>
        {action === "shift" && (
          <div style={{ ...row, marginTop: 10 }}>
            <label style={{ flex: 1, minWidth: 0, fontSize: 12 }}>Days
              <input type="number" className="chalk-input" data-testid="bulk-shift-days" value={days} onChange={(e) => setDays(e.target.value)} />
            </label>
            <label style={{ flex: 1, minWidth: 0, fontSize: 12 }}>Minutes
              <input type="number" step={15} className="chalk-input" data-testid="bulk-shift-minutes" value={minutes} onChange={(e) => setMinutes(e.target.value)} />
            </label>
          </div>
        )}
        <SectionTitle label="Note to families (optional)" />
        <textarea className="chalk-input" rows={2} data-testid="bulk-note" value={note} onChange={(e) => setNote(e.target.value)} style={{ height: "auto", minHeight: 64, paddingTop: 10 }} />
        <div data-testid="bulk-count" style={{ fontSize: 13, color: "var(--chalk-muted)", marginTop: 10 }}>
          {inRange.length} scheduled event{inRange.length === 1 ? "" : "s"} in this range.
        </div>
        <div style={{ marginTop: 14 }}>
          <ChalkActionButton testId="bulk-apply" variant={action === "cancel" ? "danger" : "primary"} loading={busy} onClick={apply}
            disabled={!inRange.length} label={`Update ${inRange.length} event${inRange.length === 1 ? "" : "s"}`} />
        </div>
      </div>
    </BottomSheet>
  );
}
